import { schedulerService } from '../services/schedulerService';
import { iterateBetweenDates, mapBetweenDates, getDateAsString } from "@/modules/common/services/timeService";
import { apiCacheService, apiCacheEnhancedService } from "@/modules/common/services";
import momentTz from "moment-timezone";
import moment from "moment";
import { retryUtil } from "@/modules/common/utils";

const meetingsArrayToDateMap = (meetings, isAmPmMode, staffTimeZone) => {
  return meetings.reduce((meetingsMap, meeting) => {
    iterateBetweenDates(meeting.dateStart, meeting.dateEnd, date => {
      const dateObj = new Date(new Date(date).toLocaleString('en-US', { timeZone: staffTimeZone }));
      const meetingDateKey = getDateAsString(dateObj);
      if (!meetingsMap[meetingDateKey]) {
        meetingsMap[meetingDateKey] = [];
      }
      meetingsMap[meetingDateKey].push(meeting);
    });
    return meetingsMap;
  }, {});
}

const getMeetingsInRange = (meetingsMap, startTime, endTime) => {
  if(!meetingsMap) {
    return []
  }
  const existingMeetingIds = new Set();

  return mapBetweenDates(startTime, endTime, date => meetingsMap[getDateAsString(new Date(date))]).reduce((acc, meetings) => {
    if (meetings) {
      const uniqueMeetings = meetings.filter(item => {
        const key = item.uid ? `${item.uid}-${item.staff_uid}` : `${item.id}-${item.staff_uid}`;
        if (existingMeetingIds.has(key)) {
          return false;
        }
        existingMeetingIds.add(key);
        return true;
      });
      return acc.concat(uniqueMeetings);
    }
    return acc;
  }, []);
}

const isThereDifferenceBetweenInRange = (oldMeetingsMap, newMeetingsMap, startTime, endTime) => {
  return mapBetweenDates(startTime, endTime, date => getDateAsString(new Date(date))).some((dateString) => {
    const res = JSON.stringify(oldMeetingsMap[dateString] || []) !== JSON.stringify(newMeetingsMap[dateString] || []);
    return res
  })
}

const assignNewMeetingsMapToOldMeetingsMap = (oldMeetingsMap, newMeetingsMap, startTime, endTime, staffUids) => {
  const staffUidsMap = staffUids.reduce((acc, uid) => {
    acc[uid] = true;
    return acc;
  }, {})
  iterateBetweenDates(startTime, endTime, (date) => {
    const dateString = getDateAsString(new Date(date));
    const oldMeetings = oldMeetingsMap[dateString] || [];
    oldMeetingsMap[dateString] = [...(newMeetingsMap[dateString] || []), ...oldMeetings.filter(item => !staffUidsMap[item.staff_uid])];
  })
}


const state = {
  fetchId: 0,
  isLoadingMeetings: false,
  isLoadingSyncMeetings: false,
  meetings: {},
  syncMeetings: {},
  currentDatesRange: {
    startTime: null,
    endTime: null,
  },
  staffUids: [],
};

const mutations = {
  setMeetings(state, {meetings, datesRange, staff_uids, isAmPmMode, forceReload, staffTimeZone}) {
    const stateMeetingsDeepCopy = JSON.parse(JSON.stringify(state.meetings));
    const newMeetingsToDateMap = meetingsArrayToDateMap(meetings, isAmPmMode, staffTimeZone);
    const areThereChanges = isThereDifferenceBetweenInRange(stateMeetingsDeepCopy, newMeetingsToDateMap, datesRange.startTime, datesRange.endTime)
    if(areThereChanges || forceReload) {
      assignNewMeetingsMapToOldMeetingsMap(stateMeetingsDeepCopy, newMeetingsToDateMap, datesRange.startTime, datesRange.endTime, staff_uids);
      state.meetings = stateMeetingsDeepCopy;
    }
    return state.meetings;
  },
  setSyncMeetings(state, {staff_id, meetings, datesRange, isAmPmMode, staffTimeZone}) {
    const stateMeetingsDeepCopy = JSON.parse(JSON.stringify(state.syncMeetings[staff_id] || {}));
    const newMeetingsToDateMap = meetingsArrayToDateMap(meetings, isAmPmMode, staffTimeZone);
    const areThereChanges = isThereDifferenceBetweenInRange(stateMeetingsDeepCopy, newMeetingsToDateMap, datesRange.startTime, datesRange.endTime)
    if(areThereChanges) {
      assignNewMeetingsMapToOldMeetingsMap(stateMeetingsDeepCopy, newMeetingsToDateMap, datesRange.startTime, datesRange.endTime, state.staffUids);

      state.syncMeetings = {
        ...state.syncMeetings,
        [staff_id]: stateMeetingsDeepCopy
      };
    }
  },
  setCurrentDatesRange(state, datesRange) {
    if (+state.currentDatesRange.startTime !== +datesRange.startTime || +state.currentDatesRange.endTime !== +datesRange.endTime) {
      state.currentDatesRange = datesRange
    }
  },
  setStaffUids(state, staff_uids) {
    if (staff_uids.length && state.staffUids.length !== staff_uids.length || state.staffUids.some((uid) => !staff_uids.includes(uid))) {
      state.staffUids = staff_uids
    }
  },
  setCachedMeetings(state, {cachedMeetings}) {
    state.meetings = cachedMeetings;
  },
  setIsLoadingMeetings(state, isLoadingMeetings) {
    state.isLoadingMeetings = isLoadingMeetings;
  },
  setIsLoadingSyncMeetings(state, isLoadingSyncMeetings) {
    state.isLoadingSyncMeetings = isLoadingSyncMeetings;
  },
  setFetchId(state, fetchId) {
    state.fetchId = fetchId;
  },
};

const actions = {
  async fetchSyncMeetings({ commit, dispatch, rootGetters}, { startTime, endTime, staff_uids, fetchId }) {
    let res;
    commit('setIsLoadingSyncMeetings', true);
    try {
      const staffTimeZone = rootGetters["accountModule/staffTimeZoneName"];
      const isAmPmMode = rootGetters["accountModule/isAmPmTimeFormat"]
      const datesRange = { startTime, endTime }
      const commonCommitParams = { datesRange, isAmPmMode, staffTimeZone }
      const fetchDataFunc = (staff_id) => async () => {
        const data = await schedulerService.fetchSyncMeetings(startTime, endTime, staff_id)
        data.busy_times.forEach(item => {
          item.startTime = momentTz.tz(item.start_time, staffTimeZone).toDate();
          item.endTime = momentTz.tz(item.end_time, staffTimeZone).toDate();
          item.dateStart ??= item.start_time
          item.dateEnd ??= item.end_time
          item.disableDrag = true;
          item.disableResize = true;
          item.provider = data.provider;
          item.id = item.uid;
        })
        commit('setSyncMeetings', {staff_id, meetings: data.busy_times, ...commonCommitParams})
        return data.busy_times
      }
      const cacheHandlerFunc = (staff_id) => (cachedMeetings) => {
        commit('setSyncMeetings', {staff_id, meetings: cachedMeetings, ...commonCommitParams});
      }

      await Promise.all(staff_uids.map(async (staff_id) => {
        return apiCacheEnhancedService.handleSchedulerDriven('CACHED_SYNC_MEETINGS', [staff_id], startTime, endTime, fetchDataFunc(staff_id), cacheHandlerFunc(staff_id), 2)
      }))
    } catch (e) {
      console.error(e)
      res = Promise.reject(e)
    } finally {
      if(state.fetchId === fetchId) {
        commit('setIsLoadingSyncMeetings', false);
      }
    }
    return res;
  },
  async fetchMeetings({ commit, dispatch ,rootGetters, state}, { startTime, endTime, staff_uids, forceReload }) {
    const fetchId = Math.random();
    let res;
    const differentUids = forceReload ? staff_uids : staff_uids.filter(uid => !state.staffUids.includes(uid))
    const didChangeTimeRange = +state.currentDatesRange.startTime !== +startTime || +state.currentDatesRange.endTime !== +endTime
    if(!didChangeTimeRange && !differentUids.length && !forceReload) {
      return
    }
    try {
      commit('setFetchId', fetchId);
      const uidsToSend = didChangeTimeRange ? staff_uids : differentUids
      dispatch('fetchSyncMeetings', { startTime, endTime, staff_uids: uidsToSend, fetchId })
      const datesRange = { startTime, endTime }
      commit('setCurrentDatesRange', datesRange)
      commit('setStaffUids', staff_uids)
      commit('setIsLoadingMeetings', true);
      const isAmPmMode = rootGetters["accountModule/isAmPmTimeFormat"]
      const commonCommitParams = { datesRange, staff_uids: uidsToSend, isAmPmMode, forceReload, staffTimeZone: rootGetters["accountModule/staffTimeZoneName"] }
      const fetchDataFunc = async () => {
        const meetings = await schedulerService.fetchMeetings(startTime, endTime, uidsToSend)
        meetings.forEach(item => {
          item.startTime = new Date(item.dateStart);
          item.endTime = new Date(item.dateEnd);
        });
        commit('setMeetings', {meetings, ...commonCommitParams})
        return meetings
      }
      const cacheHandlerFunc = (cachedMeetings) => {
        commit('setMeetings', {meetings: cachedMeetings, ...commonCommitParams})
      }

      if (rootGetters.hasFeature('no_cached_appointments')) {
        await retryUtil.retryOnFailure(fetchDataFunc, (e, retries) => {
          this.$logError('FETCH-CALENDAR-ITEMS-ERROR', { e, retries, startTime, endTime, uidsToSend, datesRange, isAmPmMode, forceReload });
          console.log(`failed to fetch meeting, retrying ${retries} more times`)
        }, 0, 3);
      } else if (rootGetters.hasFeature('local_storage_enhancement')) {
        await apiCacheEnhancedService.handleSchedulerDriven('CACHED_MEETINGS', uidsToSend, startTime, endTime, fetchDataFunc, cacheHandlerFunc, 2)
      } else {
        await apiCacheService.get({
          key: `CACHED_MEETINGS_${staff_uids}`,
          shouldLoadCache: !Object.keys(state.meetings).length,
          callFunc: fetchDataFunc,
          onSuccess: () => state.meetings,
          onCache: (cachedMeetings) => {
            commit('setCachedMeetings', {cachedMeetings})
          },
        })
      }

    } catch (e) {
      console.error(e)
      res = Promise.reject(e)
    } finally {
      if(fetchId === state.fetchId) {
        commit('setIsLoadingMeetings', false);
      }
    }
    return res;
  }
}

const getters = {
  allMeetings: (state) => Object.values(state.meetings).flat(1),
  meetingsForDisplay: (state, getters, rootState, rootGetters) => {
    const diffInDays = moment(state.currentDatesRange.endTime).diff(moment(state.currentDatesRange.startTime), 'days') + 1;
    const newStartTime = moment(state.currentDatesRange.startTime).subtract(diffInDays, 'days').toDate();
    const newEndTime = moment(state.currentDatesRange.endTime).add(diffInDays, 'days').toDate();
    const allSyncMeetings = state.staffUids.map(uid => state.syncMeetings[uid]);
    let res = [
      ...getMeetingsInRange(state.meetings, newStartTime, newEndTime),
      ...allSyncMeetings.map(syncMeetings => getMeetingsInRange(syncMeetings, newStartTime, newEndTime)).flat(1)
    ]
    res = res.filter(item => (item.subject_type === "Appointment" && item.subject?.client) || item.subject_type !== "Appointment");
    return res
  },
  isLoadingMeetings: state => state.isLoadingMeetings || state.isLoadingSyncMeetings,
}


export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
